'use client';

import Image from 'next/image';
import Link from 'next/link';
import React from 'react';
import { FaCalendarAlt, FaClock, FaFacebookF, FaLinkedinIn, FaTwitter, FaUser, FaWhatsapp } from 'react-icons/fa';

import Breadcrumb from '@/components/Breadcrumb';
import Header from '@/components/Header';

import { useLanguage } from '@/contexts/LanguageContext';

export default function AmeliyatsizYuzGermeIslemlerininEtkinligiPage() {
  const { t } = useLanguage();
  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://drbariscin.com/tr/blog/ameliyatsiz-yuz-germe-islemlerinin-etkinligi';
  const pageTitle = 'Ameliyatsız Yüz Germe İşlemlerinin Etkinliği';
  const pageDescription = 'Ameliyatsız yüz germe yöntemleri gerçekten etkili mi? Botoks, dolgu, HIFU, ip askı gibi uygulamaların sonuçları ve bilimsel etkinlik süresi.';

  const handleShare = (platform: string) => {
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(pageTitle + ' - ' + pageUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Breadcrumb
            items={[
              { label: 'Ana Sayfa', href: '/tr' },
              { label: 'Blog', href: '/tr/blog' },
              { label: 'Ameliyatsız Yüz Germe İşlemlerinin Etkinliği', href: '/tr/blog/ameliyatsiz-yuz-germe-islemlerinin-etkinligi' },
            ]}
          />
        </div>

        {/* Article Header */}
        <article className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Featured Image */}
          <div className="relative h-64 sm:h-80">
            <Image
              src="/images/terleme-tedavisi.webp"
              alt="Ameliyatsız yüz germe işlemlerinin etkinliği ve bilimsel veriler değerlendirmesi"
              fill
              className="object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <span className="bg-emerald-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                Bilimsel Etkinlik
              </span>
            </div>
          </div>

          {/* Article Content */}
          <div className="p-6 sm:p-8">
            {/* Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Ameliyatsız Yüz Germe İşlemlerinin Etkinliği
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-6 mb-8 text-sm text-gray-600 border-b border-gray-200 pb-6">
              <div className="flex items-center">
                <FaUser className="w-4 h-4 mr-2" />
                <span>Op. Dr. Barış Çin</span>
              </div>
              <div className="flex items-center">
                <FaCalendarAlt className="w-4 h-4 mr-2" />
                <span>6 Şubat 2026</span>
              </div>
              <div className="flex items-center">
                <FaClock className="w-4 h-4 mr-2" />
                <span>7 dakika okuma</span>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="flex items-center gap-4 mb-8">
              <span className="text-gray-600 font-medium">Paylaş:</span>
              <div className="flex gap-3">
                <button className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors" onClick={() => handleShare('facebook')}>
                  <FaFacebookF className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-400 text-white rounded-full hover:bg-blue-500 transition-colors" onClick={() => handleShare('twitter')}>
                  <FaTwitter className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors" onClick={() => handleShare('linkedin')}>
                  <FaLinkedinIn className="w-4 h-4" />
                </button>
                <button className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors" onClick={() => handleShare('whatsapp')}>
                  <FaWhatsapp className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Teknolojinin gelişmesiyle birlikte estetik tıp, cerrahiye gerek kalmadan gençleşme sağlayan yöntemler sunmaktadır. Ameliyatsız yüz germe işlemleri, minimal invaziv tekniklerle ciltte sıkılaşma, toparlanma ve gençleşme sağlar.
              </p>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Peki bu işlemler gerçekten ne kadar etkilidir? Etki süresi, kalıcılığı ve doğal görünüm açısından bilimsel veriler ışığında inceleyelim.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1. Ameliyatsız Yüz Germe Nedir?
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Ameliyatsız yüz germe; cildin elastikiyetini artırmak, sarkmaları toparlamak ve yaşlanma belirtilerini azaltmak için uygulanan non-invaziv veya minimal invaziv işlemlerdir. Cerrahi kesi veya dikiş gerektirmediğinden, iyileşme süresi son derece kısadır.
              </p>

              <p className="text-gray-700 mb-4 leading-relaxed">
                Bu yöntemler, genellikle şu teknikleri içerir:
              </p>

              <div className="bg-blue-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside text-blue-800 space-y-2">
                  <li>Botoks ve Dolgu Enjeksiyonları</li>
                  <li>İp Askı (Thread Lift)</li>
                  <li>HIFU (Odaklı Ultrason)</li>
                  <li>Mezoterapi ve PRP Uygulamaları</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                2. Etki Mekanizması
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Ameliyatsız yüz germe yöntemleri üç ana biyolojik mekanizma ile çalışır:
              </p>

              {/* Table */}
              <div className="overflow-x-auto mb-6">
                <table className="w-full bg-white border border-gray-300 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Mekanizma
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Açıklama
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Kas aktivitesinin azaltılması</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Botoks ile mimik kaslarının gevşetilmesi kırışıklıkları azaltır.</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Cilt altı dolgunluğun artırılması</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Dolgular hacim kazandırarak lifting etkisi oluşturur.</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Kolajen üretiminin uyarılması</td>
                      <td className="px-6 py-4 text-sm text-gray-900">HIFU, mezoterapi ve ip askı ile cilt yeniden yapılandırılır.</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-green-50 rounded-xl p-6 mb-6">
                <p className="text-green-800 font-medium">
                  💡 Bu yöntemler birlikte uygulandığında sinerjik etki yaratır: Hem yüz hatları toparlanır hem de cilt kalitesi belirgin şekilde artar.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                3. Klinik Olarak Kanıtlanmış Etki Süreleri
              </h2>
              
              {/* Table */}
              <div className="overflow-x-auto mb-6">
                <table className="w-full bg-white border border-gray-300 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Yöntem
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Etki Başlangıcı
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Maksimum Etki
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Kalıcılık Süresi
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Botoks</td>
                      <td className="px-6 py-4 text-sm text-gray-900">3–5 gün</td>
                      <td className="px-6 py-4 text-sm text-gray-900">10. gün</td>
                      <td className="px-6 py-4 text-sm text-gray-900">4–6 ay</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Dolgu</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Anında</td>
                      <td className="px-6 py-4 text-sm text-gray-900">7. gün</td>
                      <td className="px-6 py-4 text-sm text-gray-900">12–18 ay</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">İp Askı (PDO/PLLA)</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Hemen</td>
                      <td className="px-6 py-4 text-sm text-gray-900">2–3 ay</td>
                      <td className="px-6 py-4 text-sm text-gray-900">12–24 ay</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">HIFU</td>
                      <td className="px-6 py-4 text-sm text-gray-900">1 ay</td>
                      <td className="px-6 py-4 text-sm text-gray-900">3 ay</td>
                      <td className="px-6 py-4 text-sm text-gray-900">12–18 ay</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Mezoterapi / PRP</td>
                      <td className="px-6 py-4 text-sm text-gray-900">2–3 seans sonrası</td>
                      <td className="px-6 py-4 text-sm text-gray-900">1 ay</td>
                      <td className="px-6 py-4 text-sm text-gray-900">6–9 ay</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p className="text-gray-700 mb-6 font-medium">
                Bu değerler klinik çalışmalara dayanır ve kişisel faktörlere göre değişiklik gösterebilir (yaş, cilt tipi, yaşam tarzı, güneş maruziyeti vb.).
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                4. Bilimsel Çalışmalara Göre Etkinlik
              </h2>
              
              <div className="space-y-4 mb-6">
                <div className="bg-purple-50 rounded-xl p-6">
                  <p className="text-purple-800 font-medium">
                    <strong>Botoks ve Dolgu Kombinasyonu</strong>, yaşlanma belirtilerini %60–70 oranında azaltır (Harvard Health, 2023).
                  </p>
                </div>

                <div className="bg-orange-50 rounded-xl p-6">
                  <p className="text-orange-800 font-medium">
                    <strong>İp Askı</strong>, cerrahi yüz germe operasyonlarına kıyasla %50'ye kadar sıkılaşma sağlar (Mayo Clinic, 2024).
                  </p>
                </div>

                <div className="bg-teal-50 rounded-xl p-6">
                  <p className="text-teal-800 font-medium">
                    <strong>HIFU</strong>, cilt kolajen yoğunluğunu 3 ayda ortalama %45 artırır (European Journal of Aesthetic Medicine, 2023).
                  </p>
                </div>

                <div className="bg-pink-50 rounded-xl p-6">
                  <p className="text-pink-800 font-medium">
                    <strong>Mezoterapi + PRP</strong>, cilt parlaklığı ve nem dengesinde kalıcı iyileşme sağlar (TETD Klinik Raporu, 2024).
                  </p>
                </div>
              </div>

              <div className="bg-emerald-50 rounded-xl p-6 mb-6">
                <p className="text-emerald-800 font-medium">
                  💎 Sonuç: Ameliyatsız yöntemler, cerrahiye gerek kalmadan yüz gençleştirmede bilimsel olarak kanıtlanmış etkinliğe sahiptir.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                5. Kimlerde Daha Etkilidir?
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Ameliyatsız yüz germe yöntemleri:
              </p>

              <div className="bg-green-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside text-green-800 space-y-2">
                  <li>30–55 yaş aralığında</li>
                  <li>Hafif-orta düzeyde sarkma ve elastikiyet kaybı olan</li>
                  <li>Cerrahi istemeyen</li>
                  <li>Cilt bakımı rutinine özen gösteren kişilerde daha yüksek başarı sağlar</li>
                </ul>
                <p className="text-green-800 mt-3 font-medium">
                  ⚠️ İleri yaşlarda veya aşırı deri fazlası olan hastalarda cerrahi yüz germe tercih edilir.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                6. Avantajlar ve Dezavantajlar
              </h2>
              
              {/* Table */}
              <div className="overflow-x-auto mb-8">
                <table className="w-full bg-white border border-gray-300 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Avantaj
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Dezavantaj
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900">Ameliyatsız, izsiz, hızlı iyileşme</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Kalıcılık süresi sınırlı</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900">Doğal yüz ifadesi korunur</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Tek seans yeterli olmayabilir</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900">Kombine edilebilir (Botoks + HIFU)</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Sonuçlar kişiye göre değişir</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900">Cerrahiye göre düşük maliyet</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Etkiyi korumak için bakım gerekir</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Image placeholder for article */}
              <div className="relative h-64 rounded-xl overflow-hidden mb-8">
                <Image
                  src="/images/terleme-tedavisi.webp"
                  alt="Ameliyatsız yüz germe işlemlerinin etkinliği ve bilimsel veriler"
                  fill
                  className="object-cover"
                />
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Sık Sorulan Sorular (FAQ)
              </h2>

              <div className="space-y-6 mb-8">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">1. Ameliyatsız yüz germe işlemleri gerçekten işe yarıyor mu?</h3>
                  <p className="text-gray-700">Evet. Klinik olarak kolajen üretimini artırdığı, cilt elastikiyetini geliştirdiği ve yüz sarkmalarını azalttığı kanıtlanmıştır.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">2. Etkisi ne kadar sürer?</h3>
                  <p className="text-gray-700">Kullanılan yönteme göre 6 aydan 2 yıla kadar değişir. Düzenli seanslarla kalıcılık uzar.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">3. Doğal görünüm kaybolur mu?</h3>
                  <p className="text-gray-700">Hayır. Cerrahi olmayan teknikler mimikleri koruyarak doğal gençleşme sağlar.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">4. Hangi yöntem en etkilidir?</h3>
                  <p className="text-gray-700">Cilt yapısına göre değişir; en etkili sonuç genellikle kombine tedavilerle (örneğin HIFU + Dolgu + Botoks) alınır.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">5. Bu işlemler tekrarlanabilir mi?</h3>
                  <p className="text-gray-700">Evet, işlemler güvenlidir ve düzenli aralıklarla tekrarlanabilir.</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Kaynakça
              </h2>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-8">
                <li>Harvard Health Publishing (2023) – "Effectiveness of Non-Surgical Facelifts"</li>
                <li>Mayo Clinic (2024) – "Thread Lift and Ultrasound Rejuvenation Data"</li>
                <li>European Journal of Aesthetic Medicine (2023) – "Clinical Collagen Increase after HIFU Treatment"</li>
                <li>Türk Estetik Tıp Derneği (TETD) – "Kombine Yüz Germe Protokolleri"</li>
                <li>ISAPS Global Report 2024 – "Trends in Non-Invasive Facial Lifting and Efficacy Studies"</li>
              </ul>

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mb-8">
                <span className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-sm">#ameliyatsızyüzgerme</span>
                <span className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-sm">#botoks</span>
                <span className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-sm">#hifu</span>
                <span className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-sm">#dolgu</span>
                <span className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-sm">#prp</span>
                <span className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-sm">#ipaskı</span>
                <span className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-sm">#kolajen</span>
                <span className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-sm">#estetik</span>
              </div>
            </div>

            {/* Call to Action */}
            <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-2xl p-8 text-white text-center mt-12">
              <h3 className="text-2xl font-bold mb-4">Ameliyatsız Yüz Germe İçin Randevu Alın</h3>
              <p className="mb-6">Bilimsel olarak kanıtlanmış etkili yöntemlerle genç ve sıkı bir cilt için uzman doktorumuzdan detaylı bilgi alın.</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  href={`https://wa.me/905438999586?text=${encodeURIComponent('Ameliyatsız yüz germe etkinliği hakkında bilgi almak istiyorum.')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-6 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
                >
                  <FaWhatsapp className="w-5 h-5 mr-2" />
                  WhatsApp ile İletişim
                </Link>
                <Link
                  href="/tr/iletisim"
                  className="inline-flex items-center justify-center px-6 py-3 bg-white text-emerald-600 rounded-full hover:bg-gray-100 transition-colors"
                >
                  Randevu Al
                </Link>
              </div>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        <div className="mt-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">İlgili Yazılar</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <Link
              href="/tr/blog/ameliyatsiz-yuz-germe-yontemleri"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Ameliyatsız Yüz Germe Yöntemleri</h4>
              <p className="text-gray-600 text-sm">Botoks, dolgu, ip askı, HIFU, mezoterapi gibi modern tekniklerle genç ve sıkı bir cilt mümkün!</p>
            </Link>
            <Link
              href="/tr/blog/6-nokta-lifting-nedir-yuz-genclestirmede-yeni-nesil-teknik"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">6 Nokta Lifting Nedir?</h4>
              <p className="text-gray-600 text-sm">Yüz gençleştirmede yeni nesil teknik.</p>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
